import { createSlice, type PayloadAction } from '@spcc/core'
import type {
  WeighbridgeOpsState,
  WeighbridgeZone,
  WeighbridgeQueueItem,
  WeighbridgeEvent,
  ScalePanelStatus,
  ScaleZeroCheckState,
  WeightWarningState,
  OverweightConfirmState,
  GateSide,
  GateStatus,
  GateConfig,
  SocketStatus,
  WeighStage,
  WeighDirection,
  WeighbridgeFlowIdentity,
} from '../domain/types'
import type { ControllerStatusSnapshot, ControllerRecentLog } from '../api/api-types'
import { INITIAL_SCALE_PANEL_STATE, MAX_EVENT_HISTORY } from '../domain/constants'
import { isSameFlowIdentity } from '../domain/utils/flow-identity'

/**
 * Map a backend ring-buffer log entry to the equivalent frontend WeighbridgeEvent.
 * This makes boot-snapshot logs render identically to live WS events.
 * Returns null for entries that can't be meaningfully displayed (no stage, unknown type).
 */
function mapBootLogToEvent(log: ControllerRecentLog, idx: number): WeighbridgeEvent | null {
  if (log?.stage !== 'first_weight' && log?.stage !== 'second_weight') return null

  const base = {
    id: `boot-${log.id ?? idx}`,
    timestamp: log.timestamp ?? new Date().toISOString(),
    stage: log.stage as WeighStage,
    ...(log.orderNumber ? { orderNumber: log.orderNumber } : {}),
    ...(log.shipmentNumber ? { shipmentNumber: log.shipmentNumber } : {}),
    ...(log.direction ? { direction: log.direction } : {}),
  }

  switch (log.eventType) {
    case 'order_queued': {
      // Normalize the source string to the frontend union type
      const rawSrc = log.source ?? 'qr_scan'
      const source: WeighbridgeEvent['source'] =
        rawSrc === 'anpr' ? 'anpr' : rawSrc === 'manual' || rawSrc === 'manual_entry' ? 'manual_entry' : 'qr_scan'
      return { ...base, type: 'order_queued', message: 'weighbridgeOps.history.orderQueued', source }
    }

    case 'flow_queued':
      return { ...base, type: 'order_enqueued', message: 'weighbridgeOps.history.orderEnqueued' }

    case 'flow_completed':
      return { ...base, type: 'truck_departed', message: 'weighbridgeOps.history.truckDeparted' }

    case 'weight_read_received':
      return { ...base, type: 'capture_started', message: 'weighbridgeOps.history.captureStarted' }

    case 'weight_read_validated':
      return {
        ...base,
        type: 'weight_captured',
        message: 'weighbridgeOps.history.weightCaptured',
        ...(log.weight !== undefined ? { weight: log.weight } : {}),
      }

    case 'ticket_created':
      return {
        ...base,
        type: 'weight_saved',
        message: 'weighbridgeOps.history.weightSaved',
        ...(log.weight !== undefined ? { weight: log.weight } : {}),
      }

    case 'ticket_creation_failed':
      return {
        ...base,
        type: 'capture_error',
        message: 'weighbridgeOps.history.captureError',
        ...(log.errorMessage ? { errorDetail: log.errorMessage } : {}),
      }

    default:
      // Unknown event type — fall back to a generic boot_log entry with icon + colour from eventStatus
      return {
        ...base,
        type: 'boot_log',
        message: log.eventType ?? '?',
        eventStatus: log.eventStatus,
        ...(log.errorMessage ? { errorDetail: log.errorMessage } : {}),
      }
  }
}

const initialState: WeighbridgeOpsState = {
  zone: null,
  zoneFetching: false,
  zoneError: null,

  socketStatus: 'disconnected',
  connectedAt: null,
  internetConnected: null,

  firstWeight: { ...INITIAL_SCALE_PANEL_STATE },
  secondWeight: { ...INITIAL_SCALE_PANEL_STATE },

  activeQueue: [],
  manualOrderNumbers: { first_weight: '', second_weight: '' },
  manualDirections: { first_weight: 'outbound', second_weight: 'outbound' },

  firstWeightHistory: [],
  secondWeightHistory: [],
}

export const weighbridgeOpsSlice = createSlice({
  name: 'weighbridgeOps',
  initialState,
  reducers: {
    // ── Zone ────────────────────────────────────────────────────────────────
    fetchZoneRequest(state) {
      state.zoneFetching = true
      state.zoneError = null
    },
    fetchZoneSuccess(state, action: PayloadAction<WeighbridgeZone>) {
      state.zone = action.payload
      state.zoneFetching = false
    },
    fetchZoneFailure(state, action: PayloadAction<string>) {
      state.zoneError = action.payload
      state.zoneFetching = false
    },

    // ── Socket ──────────────────────────────────────────────────────────────
    setSocketStatus(state, action: PayloadAction<SocketStatus>) {
      state.socketStatus = action.payload
      if (action.payload === 'connected') {
        state.connectedAt = new Date().toISOString()
      }
    },

    setInternetConnected(state, action: PayloadAction<boolean | null>) {
      state.internetConnected = action.payload
    },

    // ── Scale panel updates (driven by WS events) ────────────────────────────
    setPanelStatus(
      state,
      action: PayloadAction<{ stage: WeighStage; status: ScalePanelStatus; weight?: number; errorMessage?: string; errorParams?: Record<string, unknown> }>,
    ) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.status = action.payload.status
      if (action.payload.status === 'idle') {
        panel.weight = undefined
        panel.errorMessage = undefined
        panel.errorParams = undefined
        panel.capturedAt = undefined
        panel.scaleZeroCheck = undefined
        panel.captureInitiatedByUser = false
        panel.isSaving = false
        return
      }
      if (action.payload.status === 'capturing') {
        // A fresh attempt is starting — drop the previous failure so the banner (which is
        // no longer gated on status === 'error') does not linger over the new capture.
        panel.errorMessage = undefined
        panel.errorParams = undefined
      }
      if (action.payload.status === 'error') {
        panel.captureInitiatedByUser = false
        panel.isSaving = false
      }
      if (action.payload.status === 'captured') {
        // Flag has been read by the saga before this reducer runs; clear it now.
        panel.captureInitiatedByUser = false
      }
      if (action.payload.weight !== undefined) {
        panel.weight = action.payload.weight
        panel.capturedAt = new Date().toISOString()
      }
      if (action.payload.errorMessage !== undefined) {
        panel.errorMessage = action.payload.errorMessage
      }
      if (action.payload.errorParams !== undefined) {
        panel.errorParams = action.payload.errorParams
      }
    },

    clearPanelWeight(state, action: PayloadAction<{ stage: WeighStage }>) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.status = 'idle'
      panel.weight = undefined
      panel.errorMessage = undefined
      panel.errorParams = undefined
      panel.capturedAt = undefined
      panel.weightWarning = undefined
      panel.overweightConfirm = undefined
      panel.captureInitiatedByUser = false
      panel.isSaving = false
    },

    // ── Scale zero-check state (before entry gate opens) ─────────────────────
    setScaleZeroCheck(
      state,
      action: PayloadAction<{ stage: WeighStage } & ScaleZeroCheckState>,
    ) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.scaleZeroCheck = {
        weight: action.payload.weight,
        attempt: action.payload.attempt,
        maxAttempts: action.payload.maxAttempts,
      }
    },
    clearScaleZeroCheck(state, action: PayloadAction<{ stage: WeighStage }>) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.scaleZeroCheck = undefined
    },

    setWeightWarning(
      state,
      action: PayloadAction<{ stage: WeighStage; warning?: WeightWarningState }>,
    ) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.weightWarning = action.payload.warning
    },

    setOverweightConfirm(
      state,
      action: PayloadAction<{ stage: WeighStage; confirm: OverweightConfirmState }>,
    ) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.overweightConfirm = action.payload.confirm
    },
    clearOverweightConfirm(state, action: PayloadAction<{ stage: WeighStage }>) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.overweightConfirm = undefined
    },
    /** Resets a panel to idle ONLY when it is stuck in error state.
     *  Safe to call on any new-truck event — will never discard a valid 'captured' weight. */
    resetPanelOnError(state, action: PayloadAction<{ stage: WeighStage }>) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      if (panel.status !== 'error') return
      panel.status = 'idle'
      panel.weight = undefined
      panel.errorMessage = undefined
      panel.errorParams = undefined
      panel.capturedAt = undefined
      panel.weightWarning = undefined
      panel.overweightConfirm = undefined
      panel.captureInitiatedByUser = false
      panel.isSaving = false
    },
    /** Saga trigger — operator clicked "Continue" in the overweight confirm panel */
    overweightContinueRequest(
      _state,
      _action: PayloadAction<WeighbridgeFlowIdentity & { stage: WeighStage }>,
    ) {},
    /** Saga trigger — operator clicked "Reject" in the overweight confirm panel */
    overweightRejectRequest(
      _state,
      _action: PayloadAction<WeighbridgeFlowIdentity & { stage: WeighStage }>,
    ) {},

    // Stage-aware dual-gate update: stage = which panel, side = entry|exit
    setPanelGate(
      state,
      action: PayloadAction<{ stage: WeighStage; side: GateSide; status: GateStatus }>,
    ) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      if (action.payload.side === 'entry') {
        panel.entryGateStatus = action.payload.status
      } else {
        panel.exitGateStatus = action.payload.status
      }
    },

    // Snapshot all 4 gate slots in a single dispatch (for device_status events)
    setGatesSnapshot(state, action: PayloadAction<{ entry: GateStatus; exit: GateStatus }>) {
      state.firstWeight.entryGateStatus = action.payload.entry
      state.firstWeight.exitGateStatus = action.payload.exit
      state.secondWeight.entryGateStatus = action.payload.entry
      state.secondWeight.exitGateStatus = action.payload.exit
    },

    // ── Queue ────────────────────────────────────────────────────────────────
    setActiveQueue(state, action: PayloadAction<WeighbridgeQueueItem[]>) {
      state.activeQueue = action.payload
    },
    upsertToQueue(state, action: PayloadAction<WeighbridgeQueueItem>) {
      const idx = state.activeQueue.findIndex(q => isSameFlowIdentity(q, action.payload))
      if (idx >= 0) {
        state.activeQueue[idx] = { ...state.activeQueue[idx], ...action.payload }
      } else {
        state.activeQueue.push({ ...action.payload, enqueuedAt: action.payload.enqueuedAt ?? new Date().toISOString() })
      }
    },
    removeFromQueue(state, action: PayloadAction<WeighbridgeFlowIdentity>) {
      state.activeQueue = state.activeQueue.filter(q => !isSameFlowIdentity(q, action.payload))
    },
    promoteQueueItem(state, action: PayloadAction<WeighbridgeFlowIdentity & { toStage: WeighStage }>) {
      const idx = state.activeQueue.findIndex(q => isSameFlowIdentity(q, action.payload))
      if (idx >= 0) {
        state.activeQueue[idx] = { ...state.activeQueue[idx], stage: action.payload.toStage }
      }
    },
    updateQueueItemDetails(
      state,
      action: PayloadAction<{
        orderNumber?: string
        shipmentNumber?: string
        direction?: WeighDirection
        driverName?: string
        vehicleLicensePlate?: string
        quantity?: number
        variantWeight?: number
        plannedWeight?: number
        firstWeight?: number
        secondWeight?: number
        detailsFetching?: boolean
      }>,
    ) {
      const idx = state.activeQueue.findIndex(q => isSameFlowIdentity(q, action.payload))
      if (idx < 0) return
      const { orderNumber: _, shipmentNumber: __, direction: ___, ...rest } = action.payload
      state.activeQueue[idx] = { ...state.activeQueue[idx], ...rest }
    },

    // ── Stage lock (Strapi error) ────────────────────────────────────────────
    stageLocked(state, action: PayloadAction<{ stage: WeighStage; lockedByOrder: string }>) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.isLocked = true
      panel.lockedByOrder = action.payload.lockedByOrder
    },
    stageUnlocked(state, action: PayloadAction<{ stage: WeighStage }>) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.isLocked = false
      panel.lockedByOrder = undefined
    },

    // ── Manual order number (per stage) ─────────────────────────────────────
    setManualOrderNumber(state, action: PayloadAction<{ stage: WeighStage; value: string }>) {
      state.manualOrderNumbers[action.payload.stage] = action.payload.value
    },

    // ── Event history (per-stage, in-memory session log) ─────────────────────
    // Only events with an explicit stage are stored.
    // Events with no stage (connected/disconnected) are dropped — the
    // ConnectionStatusBadge already shows socket state in the header.
    appendEvent(state, action: PayloadAction<WeighbridgeEvent>) {
      const event = action.payload
      if (event.stage === 'first_weight') {
        state.firstWeightHistory.push(event)
        if (state.firstWeightHistory.length > MAX_EVENT_HISTORY) {
          state.firstWeightHistory.splice(0, state.firstWeightHistory.length - MAX_EVENT_HISTORY)
        }
      } else if (event.stage === 'second_weight') {
        state.secondWeightHistory.push(event)
        if (state.secondWeightHistory.length > MAX_EVENT_HISTORY) {
          state.secondWeightHistory.splice(0, state.secondWeightHistory.length - MAX_EVENT_HISTORY)
        }
      }
      // No stage → drop (connection badge handles socket state)
    },
    clearHistory(state) {
      state.firstWeightHistory = []
      state.secondWeightHistory = []
    },

    // ── Boot snapshot (applied once on page load before socket connects) ─────
    applyBootSnapshot(state, action: PayloadAction<ControllerStatusSnapshot>) {
      const snapshot = action.payload

      // Guard: malformed payload — skip silently, the WS stream will catch up
      if (!snapshot || typeof snapshot !== 'object' || !snapshot.stages) return

      const stages = (['first_weight', 'second_weight'] as WeighStage[])
      state.firstWeight.weightWarning = undefined
      state.secondWeight.weightWarning = undefined
      state.firstWeight.overweightConfirm = undefined
      state.secondWeight.overweightConfirm = undefined

      // 1. Apply lock state per stage (fully defensive — stageData may be absent)
      for (const stage of stages) {
        const stageData = snapshot.stages?.[stage]
        if (!stageData) continue
        const panel = stage === 'first_weight' ? state.firstWeight : state.secondWeight
        if (stageData.isLocked) {
          panel.isLocked = true
          panel.lockedByOrder = stageData.lockedByOrder ?? undefined
        } else {
          panel.isLocked = false
          panel.lockedByOrder = undefined
        }
      }

      // 2. Rebuild activeQueue from stage snapshots
      const normalizeOrderDetails = (
        d?: {
          driverName?: string | null
          vehicleLicensePlate?: string | null
          quantity?: number
          variantWeight?: number
          plannedWeight?: number
        } | null,
      ): Partial<WeighbridgeQueueItem> => {
        if (!d) return {}
        return {
          ...d,
          driverName: d.driverName ?? undefined,
          vehicleLicensePlate: d.vehicleLicensePlate ?? undefined,
        }
      }

      const queueItems: WeighbridgeQueueItem[] = []
      for (const stage of stages) {
        const stageData = snapshot.stages?.[stage]
        if (!stageData) continue
        if (stageData.activeOrderNumber) {
          queueItems.push({
            orderNumber: stageData.activeOrderNumber ?? undefined,
            shipmentNumber: stageData.activeShipmentNumber ?? undefined,
            direction: stageData.activeDirection,
            stage,
            status: 'active',
            enqueuedAt: snapshot.timestamp ?? new Date().toISOString(),
            ...normalizeOrderDetails(stageData.activeOrderDetails),
          })
        }
        if (stageData.queuedOrderNumber) {
          queueItems.push({
            orderNumber: stageData.queuedOrderNumber ?? undefined,
            shipmentNumber: stageData.queuedShipmentNumber ?? undefined,
            direction: stageData.queuedDirection,
            stage,
            status: 'waiting',
            enqueuedAt: snapshot.timestamp ?? new Date().toISOString(),
            ...normalizeOrderDetails(stageData.queuedOrderDetails),
          })
        }
      }
      state.activeQueue = queueItems

      // 3. Prepend boot_log events to the correct per-stage history.
      // Logs without a stage are skipped — they have no panel to belong to.
      // We push in chronological order (oldest-first) to match how appendEvent works.
      // The selector does [...history].reverse() so newest appears at top — consistent with live events.
      const rawLogs = Array.isArray(snapshot.recentLogs) ? snapshot.recentLogs : []
      const sortedLogs = rawLogs.slice().reverse() // recentLogs is newest-first; flip to chronological

      for (let i = 0; i < sortedLogs.length; i++) {
        const log = sortedLogs[i]
        if (log?.stage !== 'first_weight' && log?.stage !== 'second_weight') continue

        const event = mapBootLogToEvent(log, i)
        if (!event) continue

        const arr = log.stage === 'first_weight' ? state.firstWeightHistory : state.secondWeightHistory
        arr.push(event)
        if (arr.length > MAX_EVENT_HISTORY) arr.splice(0, arr.length - MAX_EVENT_HISTORY)
      }
    },

    // ── Trigger actions (dispatched by UI / consumed by sagas) ──────────────
    connectSocket(_state, _action: PayloadAction<string>) {},
    disconnectSocket() {},
    captureWeightRequest(state, action: PayloadAction<WeighbridgeFlowIdentity & { stage: WeighStage }>) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.captureInitiatedByUser = true
    },
    setSavingPending(state, action: PayloadAction<{ stage: WeighStage; pending: boolean }>) {
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.isSaving = action.payload.pending
    },
    gateOpenRequest(_state, _action: PayloadAction<{ stage: WeighStage; side: GateSide; gate: GateConfig }>) {},
    gateCloseRequest(_state, _action: PayloadAction<{ stage: WeighStage; side: GateSide; gate: GateConfig }>) {},
    saveWeightRequest(state, action: PayloadAction<WeighbridgeFlowIdentity & { stage: WeighStage; weight: number }>) {
      // Disable the Save button synchronously so a second click can't fire before the saga runs.
      const panel = action.payload.stage === 'first_weight' ? state.firstWeight : state.secondWeight
      panel.isSaving = true
    },
    cancelQueueRequest(_state, _action: PayloadAction<WeighbridgeFlowIdentity & { stage?: WeighStage }>) {},
    setManualDirection(state, action: PayloadAction<{ stage: WeighStage; direction: WeighDirection }>) {
      state.manualDirections[action.payload.stage] = action.payload.direction
      state.manualOrderNumbers[action.payload.stage] = ''
    },
    initPage() {},
  },
})

export const weighbridgeOpsActions = weighbridgeOpsSlice.actions
export const weighbridgeOpsReducer = weighbridgeOpsSlice.reducer
